import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { WORK_REELS } from "../../lib/projects";
import ProjectCard from "./ProjectCard";

// WorkSection — minimal "REEL · BY · REEL." header. Project cards sit
// statically with a subtle continuous red buzz glow (handled in ProjectCard).
export const WorkSection = () => {
    const ref = useRef(null);
    const headerRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: headerRef,
        offset: ["start end", "end start"]
    });

    const titleY = useTransform(scrollYProgress, [0, 1], [60, -60]);
    const titleScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.94, 1, 1.04]);
    const ghostY = useTransform(scrollYProgress, [0, 1], ["15%", "-15%"]);
    const ghostOpacity = useTransform(
        scrollYProgress,
        [0, 0.3, 0.7, 1],
        [0, 0.05, 0.05, 0]
    );

    return (
        <section
            id="work"
            ref={ref}
            data-testid="work-section"
            className="relative overflow-hidden px-6 py-16 md:px-12 md:py-20"
        >
            <div className="mx-auto max-w-[1800px]">
                {/* Cinematic header — title only */}
                <div
                    ref={headerRef}
                    className="relative mb-14 flex flex-col items-center justify-center py-10 text-center md:mb-20 md:py-16"
                >
                    {/* Faint ghost mega word */}
                    <motion.div
                        aria-hidden
                        style={{ y: ghostY, opacity: ghostOpacity }}
                        className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
                    >
                        <span
                            className="font-display uppercase leading-none tracking-tighter text-white select-none"
                            style={{ fontSize: "clamp(7rem, 18vw, 18rem)" }}
                        >
                            SHOWREEL
                        </span>
                    </motion.div>

                    {/* REEL · BY · REEL. */}
                    <motion.div
                        style={{ y: titleY, scale: titleScale }}
                        className="relative z-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-display uppercase leading-[0.85] tracking-tight md:gap-x-10"
                    >
                        <LetterWord text="REEL" baseDelay={0.1} />
                        <DiamondSep delay={0.35} />
                        <LetterWord text="BY" baseDelay={0.45} muted />
                        <DiamondSep delay={0.6} />
                        <LetterWord text="REEL" baseDelay={0.75} italic red glow />
                        <motion.span
                            initial={{ scale: 0, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            viewport={{ once: true, margin: "0px" }}
                            transition={{ duration: 0.4, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
                            className="inline-block italic text-[#DB162F]"
                            style={{ fontSize: "clamp(3rem, 8.5vw, 10rem)" }}
                        >
                            .
                        </motion.span>
                    </motion.div>
                </div>

                {/* Project grid — 3-column layout, every card the same size. */}
                <div className="grid grid-cols-1 gap-x-8 gap-y-20 sm:grid-cols-2 md:grid-cols-3 md:gap-x-10 md:gap-y-28">
                    {WORK_REELS.map((p, i) => (
                        <ProjectCard key={p.id} project={p} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const LetterWord = ({
    text,
    baseDelay = 0,
    italic = false,
    red = false,
    muted = false,
    glow = false
}) => (
    <span
        className={`inline-flex ${italic ? "italic" : ""} ${
            red ? "text-[#DB162F]" : muted ? "text-fog-secondary" : "text-fog-primary"
        }`}
    >
        {Array.from(text).map((ch, i) => (
            <motion.span
                key={`${text}-${i}`}
                initial={{ y: 50, opacity: 0, rotate: 5 }}
                whileInView={{ y: 0, opacity: 1, rotate: 0 }}
                viewport={{ once: true, margin: "0px" }}
                transition={{
                    duration: 0.95,
                    delay: baseDelay + i * 0.07,
                    ease: [0.22, 1, 0.36, 1]
                }}
                className="inline-block"
                style={{
                    fontSize: muted
                        ? "clamp(2rem, 5.5vw, 6.5rem)"
                        : "clamp(3rem, 8.5vw, 10rem)",
                    textShadow: glow
                        ? "0 0 60px rgba(219,22,47,0.5), 0 0 22px rgba(219,22,47,0.35)"
                        : "none"
                }}
            >
                <motion.span
                    animate={{ y: [0, -4, 0] }}
                    transition={{
                        duration: 4 + (i % 3) * 0.4,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: 1 + i * 0.1
                    }}
                    className="inline-block"
                >
                    {ch}
                </motion.span>
            </motion.span>
        ))}
    </span>
);

const DiamondSep = ({ delay = 0.35 }) => (
    <motion.span
        aria-hidden
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, margin: "0px" }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        className="inline-block self-center"
        style={{ fontSize: "clamp(3rem, 8.5vw, 10rem)" }}
    >
        <motion.span
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="block h-[0.28em] w-[0.28em] rotate-45 bg-[#DB162F]"
            style={{ boxShadow: "0 0 26px rgba(219,22,47,0.6)" }}
        />
    </motion.span>
);

export default WorkSection;
